import {
  AnalyticsApi,
  AnalyticsContext,
  AnalyticsEvent,
  AnalyticsEventParam,
  AnalyticsEventType,
  AnalyticsProps,
  AnalyticsTechInitEvent,
  AnalyticsV3,
  createAnalyticsContext,
  IntegrationType,
} from '@accedo/one-plugin-sdk';
import { ErrorBoundary } from '@dynamic/ui';
import {
  createRef,
  ReactNode,
  RefObject,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { devtoolsApi } from '#/dev/devtools';
import { useConfig } from '#/hooks/useConfig';
import { useAllPluginConfigs } from '#/plugins/hooks/useAllPluginConfigs';
import {
  FeatureAnalyticsProps,
  FeatureArrayData,
} from '#/service/config/features.types';
import { getCcpaOptOutSetting } from '#/service/user/settings';
import { useBootStore } from '#/store/boot/boot.store';

import { pluginManager } from '../pluginManager';
import {
  getBoundData,
  handleIntegrationAnalyticsEvents,
  handleLegacyAnalyticsEvents,
  splitLoadedAnalyticsPluginsAndIntegrations,
  TIMEOUT,
} from './analyticsUtils';
import { useAppAnalyticsContext } from './useAppAnalyticsContext';

const Hidden = styled.div`
  display: none;
`;

Hidden.displayName = 'Hidden';

const createComponents = (
  analyticsFeatures: FeatureArrayData<FeatureAnalyticsProps>
) => {
  const newComponents: ReactNode[] = [];
  const newApiRefs: RefObject<AnalyticsApi>[] = [];
  const newConfig: FeatureArrayData<FeatureAnalyticsProps> = [];
  const ccpaOptOut = getCcpaOptOutSetting();

  analyticsFeatures.forEach((feature) => {
    const Component = pluginManager.getPluginComponent<AnalyticsProps>({
      type: IntegrationType.CLIENT_ANALYTICS,
      id: feature.key,
      optional: true,
    });

    const apiRef = createRef<AnalyticsApi>();

    newComponents.push(
      <ErrorBoundary key={feature.key} onError={Component.onError}>
        <Suspense fallback={null}>
          <Component
            apiRef={apiRef}
            ccpaOptOut={ccpaOptOut}
            databinder={getBoundData}
          />
        </Suspense>
      </ErrorBoundary>
    );
    newApiRefs.push(apiRef);
    newConfig.push(feature);
  });

  return {
    newComponents,
    newApiRefs,
    newConfig,
  };
};

// we have three kinds of buffering:
// - pre-init buffering: events that are sent before the app is booted and at least one plugin api ref is ready.
//   we store these events in `preInitBuffer` and retry them later, one time only, because at that point the event
//   buffer can take over.
// - event buffering: events that are sent after the app is booted, but not all plugins are ready yet.
//   we store event retry functions in `eventBuffer` and keep retrying them via `bufferLoop`.
// - event handler delay: when `onAnalyticsEvent` has to be recreated, there is a time period after removing the
//   old handler and adding the new handler. incoming events during this time will be re-dispatched after a timeout
//   via `onAnalyticsEventDelay`.

const preInitBuffer: Event[] = [];

let timeoutId: number | undefined;

// delay & re-dispatch incoming events while the main `onAnalyticsEvent` is not attached (being re-created)
const onAnalyticsEventDelay = (event: Event) => {
  setTimeout(() => document.dispatchEvent(event), TIMEOUT);
};

// event-specific throttling
const eventsToThrottle: Partial<
  Record<AnalyticsEventType, { ms: number; lastDispatched?: number }>
> = {
  [AnalyticsEventType.PlaybackStop]: { ms: 1000 },
  [AnalyticsEventType.ActionLogout]: { ms: 1000 },
};

// should we skip forwarding this event to the plugins for some reason?
const shouldSkipEvent = (detail?: { event: AnalyticsEventType }): boolean => {
  if (!detail) {
    // skip because it's not a valid analytics event
    return true;
  }

  const event = detail.event;
  const throttle = eventsToThrottle[event];

  if (!throttle) {
    // don't skip because throttling is not enabled for this event
    return false;
  }

  const now = Date.now();

  if (throttle.lastDispatched && now - throttle.lastDispatched < throttle.ms) {
    // skip because this event is throttled and the last dispatched time is too close
    return true;
  }
  throttle.lastDispatched = now;

  // otherwise, don't skip
  return false;
};

export const AnalyticsManager = () => {
  const [components, setComponents] = useState<ReactNode[]>([]);
  const [apiRefs, setApiRefs] = useState<
    RefObject<AnalyticsApi | AnalyticsV3.AnalyticsApi<any>>[]
  >([]);
  const [config, setConfig] = useState<FeatureArrayData<FeatureAnalyticsProps>>(
    []
  );

  const appContext = useAppAnalyticsContext();
  devtoolsApi.setContexts({ analytics: appContext });

  const { isBooted } = useBootStore();

  const { coreAnalyticsEvents } = useConfig();

  // we need a ready flag in `onAnalyticsEvent` but we don't want to re-create the callback
  // because that would mess with the retry logic.
  const isReadyRef = useRef(false);
  isReadyRef.current = isBooted && apiRefs.length > 0;

  const analytics = useAllPluginConfigs(IntegrationType.CLIENT_ANALYTICS);

  // event handler for analytics events. sending event data to all analytics plugins.
  const onAnalyticsEvent = useCallback(
    (originalEvent: Event) => {
      // typescript issue requires casting: https://github.com/microsoft/TypeScript/issues/28357
      const analyticsEvent = originalEvent as AnalyticsEvent;
      const detail = analyticsEvent.detail;
      // Recreate the context when sending an event
      const analyticsContext: AnalyticsContext = createAnalyticsContext();

      if (shouldSkipEvent(detail)) {
        return;
      }

      const { event, payload, isTechnical } = detail;

      // the technical update context event updates the app-level context only.
      if (event === AnalyticsEventType.TechUpdateContext) {
        Object.assign(appContext, payload);

        return;
      }

      const populatedContext = Object.assign(
        analyticsContext,
        appContext,
        payload
      );

      // store and retry pre-init events. see explanation at `preInitBuffer` declaration
      if (!isReadyRef.current) {
        preInitBuffer.push(originalEvent);

        return;
      }

      console.debug('AnalyticsManager - Forwarding event to plugins.', detail);

      const {
        integrationApiRefs,
        integrationConfigs,
        pluginApiRefs,
        pluginConfigs,
      } = splitLoadedAnalyticsPluginsAndIntegrations(apiRefs, config);

      // integrations - CLIENT_ANALYTICS
      handleIntegrationAnalyticsEvents(
        event,
        coreAnalyticsEvents,
        integrationApiRefs,
        integrationConfigs,
        populatedContext,
        isTechnical
      );

      // features.analytics (plugins)
      handleLegacyAnalyticsEvents(
        event,
        pluginApiRefs,
        pluginConfigs,
        populatedContext,
        analyticsEvent,
        isTechnical
      );

      // removing user-related params from the context after logging out.
      // follows the `logout` function in the root of `auth.ts` which removes the same values from the auth store.
      if (event === AnalyticsEventType.ActionLogout) {
        analyticsContext[AnalyticsEventParam.UserId] = '';
        analyticsContext[AnalyticsEventParam.UserProfile] = null;
        analyticsContext[AnalyticsEventParam.SessionId] = '';
      }

      // the first event after the init is TechInit. now that we sent that out, we can process the pre-init events.
      if (preInitBuffer.length) {
        // remove all from the buffer first, so we don't end up here again in the next run.
        preInitBuffer.splice(0).forEach(onAnalyticsEvent);
      }
    },
    [appContext, apiRefs, config, coreAnalyticsEvents]
  );

  useEffect(() => {
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  // add/remove event listener on mount/unmount
  useEffect(() => {
    document.removeEventListener('analytics', onAnalyticsEventDelay);
    document.addEventListener('analytics', onAnalyticsEvent);

    return () => {
      document.addEventListener('analytics', onAnalyticsEventDelay);
      document.removeEventListener('analytics', onAnalyticsEvent);
    };
  }, [onAnalyticsEvent]);

  // create the wrapped plugin components and api refs
  useEffect(() => {
    if (isBooted) {
      const { newComponents, newApiRefs, newConfig } = createComponents(
        analytics ?? []
      );
      setComponents(newComponents);
      setApiRefs(newApiRefs);
      setConfig(newConfig);
    }
  }, [analytics, isBooted]);

  // dispatch init event
  useEffect(() => {
    if (apiRefs.length) {
      document.dispatchEvent(new AnalyticsTechInitEvent());
    }
  }, [apiRefs]);

  return <Hidden>{components}</Hidden>;
};

AnalyticsManager.displayName = 'AnalyticsManager';
